import type { Pathname } from '../../navigation/routes.gen';
import type { SavedViewEntityType } from '../../prisma/enums';
import { APP_MODULES, type AppModule } from '../system/app';
import { REPORT_TYPES, type ReportType } from './report';
import { ORG_ROLES, type OrgRole } from './user';

/**
 * The complete set of dashboard tabs, business-owned here because the Lite tier's dashboard
 * allow-list below is defined over it. Each section maps 1:1 to a literal child route of
 * `/console/$orgId/dashboard` (see `isLiteHiddenDashboardRoutePath`).
 */
export const DASHBOARD_SECTIONS = [
    'management',
    'complaints',
    'incidents',
    'people-and-culture',
    'risks',
    'claims',
] as const;

export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

/**
 * Curium Lite — the trimmed trial tier (CUR-379).
 *
 * A Lite org (`Org.isLiteOrg`) gets a curated subset of the product; areas outside it are
 * hidden. This file is the single source of truth for what Lite includes, shared by server
 * enforcement (effective modules, `liteBlocked` meta) and client presentation (nav items,
 * route guard, configuration menu).
 */

/**
 * Modules a Lite org can effectively use. The server intersects `Org.enabledModules` with this
 * list (see `getEffectiveEnabledModules`), so an admin misconfiguration can never widen a Lite
 * org's access.
 */
export const LITE_ALLOWED_MODULES = [APP_MODULES.COMPLIANCE] as const satisfies readonly AppModule[];

/**
 * The module set enforcement actually operates on. Identity for regular orgs; the intersection
 * with `LITE_ALLOWED_MODULES` for Lite orgs. Applied at the claims chokepoint
 * (`getAuth0UserClaims`) so web tRPC, login bootstrap and the MCP bridge all agree, and mirrored
 * in `org.getOrg` so the client never sees a wider module set than the server enforces.
 */
export function getEffectiveEnabledModules(enabledModules: readonly AppModule[], isLiteOrg: boolean): AppModule[] {
    if (!isLiteOrg) {
        return [...enabledModules];
    }
    return enabledModules.filter((module) => (LITE_ALLOWED_MODULES as readonly AppModule[]).includes(module));
}

/**
 * The Lite status a *session* is treated with (CUR-379). `Org.isLiteOrg` is the raw org tier — what
 * the customer's trial includes — but a Curium **support account** (vendor staff administering a trial org
 * through an approved, audited support session, acting as themselves with delegated roles) is not
 * the customer that tier exists for. So for a support account the session behaves as a normal org:
 * no hidden areas, no obligation redaction, full compliance surface.
 *
 * Computed once at the two session sources — `getAuth0UserClaims` (server enforcement + MCP) and
 * `getOrg` (client bootstrap) — so every downstream `isLiteOrg` reader (the `liteBlocked` gate,
 * obligation redaction, client nav filter, route guard, incident and complaint obligation links) treats
 * staff as non-Lite with no per-call carve-out. The exemption lives here and nowhere else.
 *
 * The raw `Org.isLiteOrg` is deliberately left untouched: it still caps the module set via
 * `getEffectiveEnabledModules` (so a support account stays confined to the org's actual,
 * compliance-only module set) and still drives org-level analytics/billing.
 */
export function getEffectiveIsLiteOrg({
    isLiteOrg,
    isSupportAccount,
}: {
    isLiteOrg: boolean;
    isSupportAccount: boolean;
}): boolean {
    return isLiteOrg && !isSupportAccount;
}

/**
 * Areas hidden for Lite orgs (CUR-2686): product areas outside Lite and the configuration sections
 * Lite does not include. They are dropped from the nav, the command palette, favourites, the
 * configuration landing grid and idle preloading, and a direct URL renders Not found. Nothing is
 * shown crowned. Paths are route-definition paths (params uninterpolated) and match on segment
 * boundaries via `isLiteHiddenRoutePath`.
 */
export const LITE_HIDDEN_ROUTE_PREFIXES = [
    '/console/$orgId/claims',
    '/console/$orgId/risk',
    '/console/$orgId/controls',
    '/console/$orgId/mspr',
    '/console/$orgId/attestations',
    '/console/$orgId/documents',
    '/console/$orgId/tools',
    '/console/$orgId/compliance/obligations',
    '/console/$orgId/compliance/coi',
    '/console/$orgId/compliance/registers',
    '/console/$orgId/compliance/training',
    // A complaint's Communication tab (CUR-2833): the complaint itself stays, its email and SMS do not.
    '/console/$orgId/compliance/complaints/details/$id/comms',
    '/console/$orgId/workload-manager/emails',
    '/console/$orgId/configuration/org-settings/system',
    '/console/$orgId/configuration/org-settings/communication',
    '/console/$orgId/configuration/org-settings/trash',
    '/console/$orgId/configuration/claims-products',
    '/console/$orgId/configuration/financials',
    // The whole Compliance section, Training included (CUR-2687): a single prefix so any
    // Compliance child added later is hidden for Lite by default.
    '/console/$orgId/configuration/compliance',
    '/console/$orgId/configuration/risk',
    '/console/$orgId/configuration/vendor-management',
    '/console/$orgId/configuration/monitoring',
    '/console/$orgId/configuration/api-integrations',
] as const satisfies readonly Pathname[];

/**
 * Dashboard tabs a Lite org sees (CUR-2684). Every other tab is hidden, not crowned — an
 * allow-list, so a dashboard added later stays hidden for Lite until it is added here on purpose.
 */
export const LITE_ENABLED_DASHBOARD_SECTIONS = [
    'complaints',
    'incidents',
] as const satisfies readonly DashboardSection[];

/** True when a dashboard section is shown on the Lite tier. */
export function isLiteEnabledDashboardSection(section: DashboardSection): boolean {
    return (LITE_ENABLED_DASHBOARD_SECTIONS as readonly DashboardSection[]).includes(section);
}

/**
 * Every kind of item Home renders: My items cards and tiles, the focus view's tiles and legend,
 * quick actions, saved views and the right column. Each Home element declares the kind(s) it
 * belongs to, and the Lite tier filters on them (CUR-2683).
 */
export type HomeItem =
    | 'complaints'
    | 'incidents'
    | 'tasks'
    | 'claims'
    | 'obligations'
    | 'controls'
    | 'tests'
    | 'risks'
    | 'emails'
    | 'attestations'
    | 'conflicts'
    | 'training';

/**
 * The Home item kinds a Lite org sees (CUR-2683). An allow-list, so a Home item added later stays
 * hidden for Lite unless it is added here on purpose. A Home element made of several kinds (Smart
 * intake: complaints and incidents) shows when at least one of them is enabled.
 */
export const LITE_ENABLED_HOME_ITEMS = ['complaints', 'incidents', 'tasks'] as const satisfies readonly HomeItem[];

/** True when Home shows items of this kind to a Lite org. */
export function isLiteEnabledHomeItem(item: HomeItem): boolean {
    return (LITE_ENABLED_HOME_ITEMS as readonly HomeItem[]).includes(item);
}

/**
 * True when the route path is the literal tab route of a dashboard section hidden on the Lite
 * tier (`/console/$orgId/dashboard/<section>`). The bare `/console/$orgId/dashboard` index and
 * unrelated paths are never hidden. Paths are route-definition paths (params uninterpolated).
 */
export function isLiteHiddenDashboardRoutePath(routePath: string): boolean {
    return DASHBOARD_SECTIONS.some(
        (section) => routePath === `/console/$orgId/dashboard/${section}` && !isLiteEnabledDashboardSection(section),
    );
}

function matchesRoutePrefix(routePath: string, prefix: string): boolean {
    return routePath === prefix || routePath.startsWith(`${prefix}/`);
}

/**
 * True when the path is inside an area hidden for Lite orgs (`LITE_HIDDEN_ROUTE_PREFIXES`). Matches on
 * segment boundaries so `/claims` never captures an unrelated `/claims-foo` sibling. Paths are
 * route-definition paths (params uninterpolated).
 */
export function isLiteHiddenRoutePath(routePath: string): boolean {
    return LITE_HIDDEN_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(routePath, prefix));
}

/**
 * The list page each saved-view entity type belongs to. A saved view is a shortcut into that page,
 * so a Lite org has no use for the saved views of a page hidden for Lite (CUR-2686). Typed as a full
 * record, so a new entity type must name its page here.
 */
const SAVED_VIEW_LIST_ROUTES = {
    CLAIM: '/console/$orgId/claims',
    INCIDENT: '/console/$orgId/compliance/incidents',
    COMPLAINT: '/console/$orgId/compliance/complaints',
    RISK: '/console/$orgId/risk/register',
    CONTROL: '/console/$orgId/controls/register',
    OBLIGATION: '/console/$orgId/compliance/obligations',
    USER_ATTESTATION: '/console/$orgId/attestations/user-attestations',
    USER_ATTESTATION_SCHEDULE: '/console/$orgId/attestations/scheduled-attestations',
    USER: '/console/$orgId/configuration/org-settings/users',
    DOCUMENT: '/console/$orgId/documents',
} as const satisfies Record<SavedViewEntityType, Pathname>;

/** True when the saved views of this entity type belong to a page hidden for Lite orgs. */
export function isLiteHiddenSavedViewEntityType(entityType: SavedViewEntityType): boolean {
    return isLiteHiddenRoutePath(SAVED_VIEW_LIST_ROUTES[entityType]);
}

/**
 * Report types a Lite org can generate (CUR-2686). An allow-list, so a report type added later stays
 * unavailable to Lite unless it is added here on purpose. `report.generateReport` refuses every other
 * type for a Lite org, and the report picker offers only these.
 */
export const LITE_ENABLED_REPORT_TYPES = [
    REPORT_TYPES.COMPLAINTS,
    REPORT_TYPES.INCIDENTS,
    REPORT_TYPES.COMPLAINTS_ASIC_2023,
    REPORT_TYPES.IBCCC_BREACH_2025,
] as const satisfies readonly ReportType[];

/** True when a Lite org can generate reports of this type. */
export function isLiteEnabledReportType(reportType: ReportType): boolean {
    return (LITE_ENABLED_REPORT_TYPES as readonly ReportType[]).includes(reportType);
}

/**
 * Organisation roles a Lite session can assign (CUR-2686). An allow-list, so a role added later stays
 * unassignable on Lite unless it is added here on purpose. `training_manager` and `supplier` are left
 * out: the areas they exist for are not part of Lite. Users who already hold one keep it.
 */
export const LITE_ENABLED_ORG_ROLES = [
    ORG_ROLES.ORG_ADMIN,
    ORG_ROLES.USER_MANAGER,
    ORG_ROLES.USER,
    ORG_ROLES.SHARED_EDIT_USER,
] as const satisfies readonly OrgRole[];

/** True when a Lite session can assign this organisation role. */
export function isLiteEnabledOrgRole(role: OrgRole): boolean {
    return (LITE_ENABLED_ORG_ROLES as readonly OrgRole[]).includes(role);
}
